// The PRE-CONTRACT half of LOLER intake, in one place, shared by both surfaces that can file a
// report. The five inputs themselves are the companion PreContractFields.tsx; the decision lives
// here, in a plain module, so the server's own parity test can read the canonical field list
// (server/tsconfig.json sets no --jsx).
//
// The two surfaces: the manual New report modal (LolerReportsPage) and the Gmail sweep review queue
// (LolerGmailSweepModal). Spec section 4's lead-gen case -- a thorough-examination report arrives
// from someone who is not a customer yet, and its defects double as a remedial quote and as the
// lever to put them on a maintenance contract.
//
// WHY THIS IS SHARED RATHER THAN WRITTEN TWICE. The five customer/site fields are submitted ONLY by
// the pre-contract branch; the lift branch sends assetId alone and the server nulls the free text
// (loler.routes.ts: `customerName: assetId ? null : ...`). So a surface that forgets one field does
// not fail loudly, it silently drops a detail the model already read off the report. That is exactly
// what happened to customerPhone, which the extractor read and the schema accepted while no form
// anywhere had a field for it (Session ALG). With one builder and one field set there is no second
// place for the next one to go missing.
//
// Nothing here talks to the server. The pre-contract branch is resolved by the real office route,
// POST /api/loler/reports, which attaches the enquiry to a Prospect through prospectIntake.ts
// (prospectFromEnquiry + matchProspect). This module only decides WHICH fields that one route is
// given, so neither surface can reimplement the branch.
import type { ExtractedHeader, LolerReportCreateInput } from "../../lib/api";

// Which of the two things a report is being filed against. "lift" is an existing lift on the
// register; "prospect" is a pre-contract enquiry from someone with no Asset/Site/Customer row yet.
export type LolerIntakeMode = "lift" | "prospect";

// The pre-contract enquirer's details, as typed into a form: always strings, never undefined, so a
// controlled input never flips between controlled and uncontrolled.
export interface PreContractDetails {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  siteName: string;
  siteAddress: string;
}

export const EMPTY_PRE_CONTRACT: PreContractDetails = {
  customerName: "",
  customerEmail: "",
  customerPhone: "",
  siteName: "",
  siteAddress: "",
};

// The five fields the AI extraction reads for this branch, and the ONLY five it can fill. Kept as a
// list so the fields component, the builder and their tests all walk the same set.
export const PRE_CONTRACT_FIELDS = ["customerName", "customerEmail", "customerPhone", "siteName", "siteAddress"] as const;

// Pre-fill from an extracted header. A field the model did not find stays empty for the office to
// type; nothing is ever guessed here.
export function preContractFromHeader(h: ExtractedHeader | null | undefined): PreContractDetails {
  const src = h ?? {};
  return {
    customerName: src.customerName ?? "",
    customerEmail: src.customerEmail ?? "",
    customerPhone: src.customerPhone ?? "",
    siteName: src.siteName ?? "",
    siteAddress: src.siteAddress ?? "",
  };
}

// Does the extraction name a customer or a site, i.e. details that ONLY the pre-contract branch
// saves? This is what makes it worth telling the office the report may not be for a lift on file.
//
// customerEmail is deliberately NOT in this test, and that is a decision rather than an oversight: a
// report giving an email address and no name, no phone, no site name and no address does not
// identify an enquirer well enough to be worth prompting about, and the branch's own validation
// would refuse it anyway (it needs a site or a customer name). Session ALG's original predicate on
// the manual page read exactly this way and is preserved unchanged.
export function hasLeadGenDetails(h: ExtractedHeader | null | undefined): boolean {
  const src = h ?? {};
  return Boolean(src.customerName || src.customerPhone || src.siteName || src.siteAddress);
}

// The fields that say WHO or WHAT a report is for. Everything else on the create body (dates,
// inspector, reference, completeness, defects) is identical on both branches.
export type LolerIntakeFields = Pick<
  LolerReportCreateInput,
  "assetId" | "customerName" | "customerEmail" | "customerPhone" | "siteName" | "siteAddress"
>;

export type LolerIntakeResult = { ok: true; fields: LolerIntakeFields } | { ok: false; message: string };

/**
 * Is this a shape the server will accept as an email address?
 *
 * A client MIRROR of the server's rule, in the house style (quotePricing, maintenanceTerms,
 * checklistFrequency all do this), bound by a parity test in lolerPreContract.test.ts rather than
 * trusted. zod is a dependency of this repo but has never been imported into the CLIENT bundle, and
 * pulling it in for one field would be a heavier change than the check is worth.
 *
 * WHY THE CHECK EXISTS AT ALL. createReportSchema refuses a malformed customerEmail with
 * `{error: "invalid_request", details: {customerEmail: [...]}}` and NO message, so apiRequest reads
 * the code and the card renders the literal string "invalid_request" with none of the five fields
 * named and the details discarded. The office is left pressing Create on an unchanged form. The
 * value reaching that field is either typed by hand or read off a PDF by a model, so a plain
 * sentence naming the field is worth more than a round trip.
 *
 * The parity test asserts the direction that matters: anything this ACCEPTS, the server accepts. The
 * reverse is allowed to differ, because a client stricter than the server only ever refuses early
 * with wording of its own.
 */
export function looksLikeEmail(value: string): boolean {
  const v = value.trim();
  if (!v || v.length > 200) return false;
  // No whitespace, exactly one @, a dotted domain, and no leading/trailing/doubled dot in either
  // half. That is the ground zod's own default email rule covers, minus its unicode allowances.
  if (!/^[^\s@]+@[^\s@]+$/.test(v)) return false;
  const [local, domain] = v.split("@") as [string, string];
  if (/^\.|\.$|\.\./.test(local) || /^\.|\.$|\.\./.test(domain)) return false;
  if (!/^[A-Za-z0-9._%+'-]+$/.test(local)) return false;
  return /^[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/.test(domain);
}

// Build the branch. Pure, and the single decision about which fields a create carries.
//
// A blank field is sent as undefined rather than as "": the create body is a sparse patch of what
// the office actually gave, which is what the manual page has always sent.
export function buildIntakeFields(
  mode: LolerIntakeMode,
  input: { assetId: string; details: PreContractDetails },
): LolerIntakeResult {
  if (mode === "lift") {
    const assetId = input.assetId.trim();
    if (!assetId) return { ok: false, message: "Choose a lift, or switch to a pre-contract enquiry." };
    return { ok: true, fields: { assetId } };
  }
  const d = input.details;
  // The server's own rule (createReportSchema's refine, and the route's `!assetId && (siteName ||
  // customerName)` gate): without one of these two the enquiry identifies nobody, no Prospect can be
  // built from it, and the create is refused. Checked here so the office is told in the form rather
  // than by a 400 with no wording of its own.
  if (!d.siteName.trim() && !d.customerName.trim()) {
    return { ok: false, message: "Enter a site or customer for the pre-contract enquiry." };
  }
  if (d.customerEmail.trim() && !looksLikeEmail(d.customerEmail)) {
    return { ok: false, message: "That customer email does not look like an email address. Correct it, or clear it." };
  }
  return {
    ok: true,
    fields: {
      customerName: d.customerName.trim() || undefined,
      customerEmail: d.customerEmail.trim() || undefined,
      customerPhone: d.customerPhone.trim() || undefined,
      siteName: d.siteName.trim() || undefined,
      siteAddress: d.siteAddress.trim() || undefined,
    },
  };
}

// There is deliberately NO separate "can this be submitted" helper. A surface asks buildIntakeFields
// ONCE per render and uses the one answer for both jobs: whether the button is live, and, when it is
// not, the sentence saying why. A second predicate is a second chance for the two to disagree, and
// the refusal message is the thing the office actually needs on screen.
